/**
 * CorvanaAIModule — the dedicated Corvana AI tab for QSR. Opens with prioritised
 * STORE-LEVEL actions (not generic insights). Assigning an action creates a REAL
 * tracked task (owner + due time) that persists, carries over and stays until
 * actioned (see AssignedActions). Corvana AI also lives on every card via AskCorvana.
 */
import React from 'react';
import { Sparkles, AlertTriangle, TrendingUp, Timer, Trash2, ArrowRight, Brain, Check } from 'lucide-react';
import { toast } from 'sonner';
import AddOnsPanel from './AddOnsPanel';
import AssignedActions from './AssignedActions';
import MorningBriefing from './MorningBriefing';
import ManagerDigest from './ManagerDigest';
import useQsrTasks from './useQsrTasks';
import { computeDue } from './qsrTasks';
import { QSR_STORES, money } from './qsrDemoData';

const urgencyStyle = {
  high: 'bg-rose-50 text-rose-700 border-rose-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-slate-100 text-slate-600 border-slate-200',
};

function buildActions() {
  const s = Object.fromEntries(QSR_STORES.map((x) => [x.id, x]));
  return [
    { id: 'a1', store: s.southport, icon: Timer, urgency: 'high', title: 'Add a runner to Southport dinner (5–8pm)', why: `Dinner speed of service is ${s.southport.sosByDaypart[4].toFixed(1)}m — the network's slowest window.`, impact: s.southport.weeklySales * 0.06 },
    { id: 'a2', store: s.southport, icon: Trash2, urgency: 'high', title: 'Tighten Southport prep & remakes', why: `Waste is ${s.southport.wastePct}% vs <2.5% target; remakes are the top driver.`, impact: 2580 },
    { id: 'a3', store: s.penrith, icon: TrendingUp, urgency: 'medium', title: 'Roll combo upsell script at Penrith', why: `Combo attachment is ${s.penrith.comboAttach}% — a 5pt lift is high-margin.`, impact: s.penrith.weeklySales * 0.018 },
    { id: 'a4', store: s.penrith, icon: AlertTriangle, urgency: 'medium', title: 'Review Penrith lunch labour', why: `Labour hits ${Math.max(...s.penrith.labourByDaypart)}% at peak while SPLH trails Footscray.`, impact: 640 },
    { id: 'a5', store: s.footscray, icon: TrendingUp, urgency: 'low', title: 'Protect Footscray as the network benchmark', why: 'Top SPLH & accuracy — capture its dinner playbook and clone it.', impact: 0 },
  ];
}

export default function CorvanaAIModule({ onSelectStore, addons, onToggleAddon, businessId, taskApi }) {
  const actions = buildActions();
  const totalUpside = actions.reduce((s, a) => s + a.impact, 0);
  const fallback = useQsrTasks(businessId, !taskApi);
  const { tasks, add, toggle, hasOpen } = taskApi || fallback;

  const assign = (a) => {
    add({ actionId: a.id, title: a.title, owner: a.store.manager, store: a.store.name, priority: a.urgency, due: computeDue(a.urgency) });
    toast.success('Assigned', { description: `"${a.title}" → ${a.store.manager} (${a.store.name}).` });
  };

  return (
    <div className="space-y-4 qsr-stage p-3 sm:p-5" data-testid="corvana-ai-module">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 to-slate-700 text-white p-5 sm:p-6 shadow-2xl ring-1 ring-white/10">
        <div className="absolute -top-16 -right-10 w-56 h-56 rounded-full bg-emerald-500/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 text-emerald-300 text-xs font-semibold mb-1"><Brain className="w-4 h-4" /> Corvana AI</div>
          <h2 className="text-2xl font-semibold tracking-tight">Your store playbook for today</h2>
          <p className="text-sm text-slate-300 mt-1">Prioritised, store-level actions — ranked by margin impact.</p>
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-sm font-semibold backdrop-blur">
            <Sparkles className="w-4 h-4 text-emerald-300" /> ~{money(totalUpside)}/wk upside across {QSR_STORES.length} stores
          </div>
        </div>
      </div>

      <MorningBriefing actions={actions.slice(0, 3)} businessId={businessId} onAssign={assign} isAssigned={hasOpen} onSelectStore={onSelectStore} />

      <div className="space-y-2.5">
        {actions.map((a) => {
          const assigned = hasOpen(a.id);
          return (
            <div key={a.id} className="rounded-2xl qsr-card p-4 shadow-sm flex items-start gap-3" data-testid={`ai-action-${a.id}`}>
              <div className="p-2 rounded-xl bg-slate-100 text-slate-600 shrink-0"><a.icon className="w-4 h-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${urgencyStyle[a.urgency]}`}>{a.urgency}</span>
                  <button onClick={() => onSelectStore?.(a.store)} className="text-xs font-semibold text-slate-500 hover:text-slate-800 underline-offset-2 hover:underline">{a.store.name}</button>
                  {a.impact > 0 && <span className="text-[11px] font-semibold text-emerald-600">+{money(a.impact)}/wk</span>}
                </div>
                <div className="font-semibold text-sm text-slate-900 mt-1">{a.title}</div>
                <p className="text-xs text-slate-500 mt-0.5">{a.why}</p>
              </div>
              <button
                onClick={() => assign(a)}
                disabled={assigned}
                className={`shrink-0 inline-flex items-center gap-1 rounded-lg text-xs font-semibold px-3 py-2 transition-colors ${assigned ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 cursor-default' : 'bg-slate-900 text-white hover:bg-slate-800'}`}
                data-testid={`ai-apply-${a.id}`}
              >
                {assigned ? <><Check className="w-3.5 h-3.5" /> Assigned</> : <>Assign <ArrowRight className="w-3.5 h-3.5" /></>}
              </button>
            </div>
          );
        })}
      </div>

      <AssignedActions tasks={tasks} onToggle={toggle} />

      <ManagerDigest tasks={tasks} />

      <AddOnsPanel addons={addons} onToggle={onToggleAddon} />
    </div>
  );
}
